import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import VolunteerNav from '../components/VolunteerNav';
import Icons from '../components/Icons';

const renderDescription = (description) => {
  if (!description) return null;
  return description.split('\n').map((line, i) => {
    const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
    if (urlMatch) {
      const parts = line.split(/(https?:\/\/[^\s]+)/);
      return (
        <span key={i}>
          {parts[0]}
          <a href={parts[1]} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{parts[1]}</a>
          {parts[2]}{'\n'}
        </span>
      );
    }
    return <span key={i}>{line}{'\n'}</span>;
  });
};

// Color/icon scheme for each event type - shown in both the month-view chips
// and the legend, and reused for the list-view date block.
const EVENT_TYPE_STYLES = {
  meeting: { label: 'Meeting', bg: 'bg-primary-100', text: 'text-primary-700', border: 'border-primary-200', dot: 'bg-primary-500', icon: Icons.calendar },
  volunteer_event: { label: 'Volunteer Event', bg: 'bg-success-100', text: 'text-success-700', border: 'border-success-200', dot: 'bg-success-500', icon: Icons.hand },
  training: { label: 'Training', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: Icons.graduationCap },
  fundraiser: { label: 'Fundraiser', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500', icon: Icons.gift },
  other: { label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-400', icon: Icons.tag },
};
const getEventTypeStyle = (type) => EVENT_TYPE_STYLES[type] || EVENT_TYPE_STYLES.other;

const Calendar = () => {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('list');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  const currentMonth = selectedDate.getMonth() + 1;
  const currentYear = selectedDate.getFullYear();

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, view]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');

      // Both views are scoped to the same selected month/year - List view
      // just splits that month's events into Upcoming/Past sections client-side.
      const params = new URLSearchParams({
        month: currentMonth,
        year: currentYear,
        view: view
      });
      const response = await api.get(`/calendar/events?${params}`);

      if (response.data.success) {
        setEvents(response.data.events);
      } else {
        setError(response.data.message || 'Failed to load events');
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = async (event) => {
    try {
      const response = await api.get(`/calendar/events/${event.id}`);

      if (response.data.success) {
        setSelectedEvent(response.data.event);
        setShowEventModal(true);
      } else {
        setError(response.data.message || 'Failed to load event details');
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError('Failed to load event details');
    }
  };

  const handleRSVP = async (eventId, status, notes = '') => {
    try {
      setRsvpLoading(true);

      const response = await api.post(`/calendar/events/${eventId}/rsvp`, {
        status,
        notes
      });

      if (response.data.success) {
        fetchEvents();
        if (selectedEvent) {
          handleEventClick(selectedEvent);
        }
      } else {
        setError(response.data.message || 'Failed to update RSVP');
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      setError('Failed to update RSVP');
    } finally {
      setRsvpLoading(false);
    }
  };

  const buildGoogleCalendarUrl = (event) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toGoogleDate = (dateStr, timeStr) => {
      const [y, m, d] = dateStr.split('T')[0].split('-');
      if (!timeStr || event.is_all_day) return `${y}${m}${d}`;
      const [h, min] = timeStr.split(':');
      return `${y}${m}${d}T${pad(h)}${pad(min)}00`;
    };
    const start = toGoogleDate(event.start_date, event.start_time);
    const endDate = event.end_date || event.start_date;
    let end = toGoogleDate(endDate, event.end_time || event.start_time);
    // All-day: Google needs end date to be day after
    if (event.is_all_day) {
      const d = new Date(endDate.split('T')[0]);
      d.setDate(d.getDate() + 1);
      end = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}`;
    }
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title || '',
      dates: `${start}/${end}`,
      details: event.description || '',
      location: event.location || '',
    });
    return `https://calendar.google.com/calendar/render?${params}`;
  };

  const buildOutlookUrl = (event) => {
    const toISOLocal = (dateStr, timeStr) => {
      const d = dateStr.split('T')[0];
      if (!timeStr || event.is_all_day) return `${d}`;
      return `${d}T${timeStr.slice(0,5)}:00`;
    };
    const params = new URLSearchParams({
      rru: 'addevent',
      startdt: toISOLocal(event.start_date, event.start_time),
      enddt: toISOLocal(event.end_date || event.start_date, event.end_time || event.start_time),
      subject: event.title || '',
      body: event.description || '',
      location: event.location || '',
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params}`;
  };

  const exportEvent = async (eventId) => {
    try {
      // Detect user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await api.get(`/calendar/events/${eventId}/export`, {
        params: { timezone: userTimezone },
        responseType: 'blob'
      });

      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `event-${eventId}.ics`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting event:', error);
      setError('Failed to export event');
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  // Parse a date as local time to avoid timezone shifts
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return new Date(year, month - 1, day);
  };

  const formatDate = (dateStr) => {
    return parseLocalDate(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Short month + day, for the list-view date block
  const getDateParts = (dateStr) => {
    const date = parseLocalDate(dateStr);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      day: date.getDate()
    };
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to check if an event is in the past
  const isEventInPast = (event) => {
    if (!event) return false;
    const now = new Date();

    // Use end_date and end_time if available, otherwise use start_date and start_time
    const dateToCheck = event.end_date || event.start_date;
    const timeToCheck = event.end_time || event.start_time;

    // Parse event date in local timezone
    const dateParts = dateToCheck.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
    const day = parseInt(dateParts[2]);

    // If event has a time, parse it and create date with time
    if (timeToCheck) {
      const [hours, minutes] = timeToCheck.split(':');
      const eventDateTime = new Date(year, month, day, parseInt(hours), parseInt(minutes), 0);
      return eventDateTime < now;
    } else {
      // If no time specified, consider event lasts all day (until end of day)
      const eventDateTime = new Date(year, month, day, 23, 59, 59, 999);
      return eventDateTime < now;
    }
  };

  const hasPostEventInfo = (event) =>
    !!(event.agenda || event.post_event_notes || event.video_recording_url || event.post_event_summary);

  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const dayEvents = events.filter(event => {
        const eventDate = parseLocalDate(event.start_date);
        return eventDate.toDateString() === currentDate.toDateString();
      });

      days.push({
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === month,
        isToday: currentDate.toDateString() === new Date().toDateString(),
        events: dayEvents
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const monthName = selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // A single event card for list view - shared by the Upcoming and Past sections
  const renderListCard = (event, isPast) => {
    const style = getEventTypeStyle(event.event_type);
    const { month, day } = getDateParts(event.start_date);
    const showPostEventInfo = isPast && hasPostEventInfo(event);

    return (
      <div
        key={event.id}
        onClick={() => handleEventClick(event)}
        className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer p-5"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            <div className={`w-12 h-14 rounded-lg flex flex-col items-center justify-center flex-shrink-0 border ${style.bg} ${style.border}`}>
              <span className={`text-xs font-medium ${style.text}`}>{month}</span>
              <span className={`text-lg font-bold ${style.text}`}>{day}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-1 break-words">{event.title}</h3>
              <div className="flex items-center space-x-3 text-xs text-gray-500 mb-2 flex-wrap gap-y-1">
                {!event.is_all_day ? (
                  <span className="flex items-center space-x-1">
                    {Icons.clock('w-3.5 h-3.5')}
                    <span>{formatTime(event.start_time)}{event.end_time && ` - ${formatTime(event.end_time)}`}</span>
                  </span>
                ) : (
                  <span className="text-purple-600 font-medium">All Day</span>
                )}
                {event.location && (
                  <span className="flex items-center space-x-1">
                    {Icons.locationPin('w-3.5 h-3.5')}
                    <span className="break-words">{event.location}</span>
                  </span>
                )}
              </div>
              {event.description && (
                <p className="text-sm text-gray-500 line-clamp-2 break-words">{event.description}</p>
              )}
            </div>
          </div>

          <div className="ml-4 flex flex-col items-end space-y-2 flex-shrink-0">
            <span className="text-xs text-gray-400">{event.confirmed_signups} signed up</span>
            {!isPast && event.user_rsvp_status && (
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                event.user_rsvp_status === 'signed_up'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {event.user_rsvp_status === 'signed_up' ? "RSVP'd Yes" : "RSVP'd No"}
              </span>
            )}
            {showPostEventInfo && (
              <button
                onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                className="bg-warning-600 hover:bg-warning-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap"
              >
                Post Event Info
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <VolunteerNav currentPage="Calendar" />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 h-screen">
        <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <div className="flex items-center space-x-2.5 mb-1">
                {Icons.calendar('w-5 h-5 text-gray-400')}
                <h1 className="text-xl font-bold text-gray-900">Calendar</h1>
              </div>
              <p className="text-sm text-gray-500">Events and meetings for your organization</p>
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-lg p-1">
              {['month', 'list'].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-3.5 py-1.5 text-sm rounded-md font-medium transition-colors capitalize ${
                    view === v ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Month nav - drives both views, since List is now scoped to the selected month too */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-white rounded-lg border border-gray-200 transition-colors"
            >
              {Icons.chevronLeft()}
            </button>
            <h2 className="text-lg font-semibold text-gray-900">{monthName}</h2>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-white rounded-lg border border-gray-200 transition-colors"
            >
              {Icons.chevronRight()}
            </button>
          </div>

          {/* Calendar Views */}
          {view === 'month' ? (
            <div>
              {/* Event type legend */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                {Object.values(EVENT_TYPE_STYLES).map((style) => (
                  <div key={style.label} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`}></span>
                    {style.icon('w-3.5 h-3.5 text-gray-400')}
                    <span>{style.label}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Calendar Header */}
                <div className="grid grid-cols-7 border-b border-gray-200">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="px-3 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7">
                  {generateCalendarDays().map((day, index) => (
                    <div
                      key={index}
                      className={`min-h-[100px] p-2 border-b border-r border-gray-100 ${
                        !day.isCurrentMonth ? 'bg-gray-50/50' : ''
                      } ${day.isToday ? 'bg-primary-50/50' : ''}`}
                    >
                      <div className={`text-sm mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                        day.isToday ? 'bg-primary-600 text-white font-bold' : 'text-gray-700'
                      } ${!day.isCurrentMonth ? 'text-gray-300' : ''}`}>
                        {day.date.getDate()}
                      </div>

                      {day.events.slice(0, 2).map(event => {
                        const style = getEventTypeStyle(event.event_type);
                        return (
                          <div
                            key={event.id}
                            onClick={() => handleEventClick(event)}
                            className={`text-xs px-1.5 py-0.5 mb-1 rounded border cursor-pointer hover:opacity-80 truncate ${style.bg} ${style.text} ${style.border}`}
                          >
                            {event.is_all_day ? '' : formatTime(event.start_time) + ' '}
                            {event.title}
                          </div>
                        );
                      })}

                      {day.events.length > 2 && (
                        <div className="text-xs text-gray-400 pl-1">
                          +{day.events.length - 2} more
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (() => {
            const upcomingEvents = events.filter(e => !isEventInPast(e));
            const pastEvents = events.filter(e => isEventInPast(e));

            return (
              <div className="space-y-8">
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Upcoming</h3>
                  {upcomingEvents.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500 text-sm">
                      No upcoming events this month
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {upcomingEvents.map(event => renderListCard(event, false))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Past</h3>
                  {pastEvents.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500 text-sm">
                      No past events this month
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pastEvents.map(event => renderListCard(event, true))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Event Details Modal */}
        {showEventModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 break-words pr-4">{selectedEvent.title}</h2>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  {Icons.close('w-5 h-5')}
                </button>
              </div>

              <div className="px-6 py-5 max-h-[calc(100vh-14rem)] overflow-y-auto">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    {Icons.calendar('w-4 h-4 text-gray-400 flex-shrink-0')}
                    <span>
                      {formatDate(selectedEvent.start_date)}
                      {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && (
                        <span> - {formatDate(selectedEvent.end_date)}</span>
                      )}
                    </span>
                  </div>

                  {!selectedEvent.is_all_day && (
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      {Icons.clock('w-4 h-4 text-gray-400 flex-shrink-0')}
                      <span>
                        {formatTime(selectedEvent.start_time)}
                        {selectedEvent.end_time && ` - ${formatTime(selectedEvent.end_time)}`}
                      </span>
                    </div>
                  )}

                  {selectedEvent.location && (
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      {Icons.locationPin('w-4 h-4 text-gray-400 flex-shrink-0')}
                      <span className="break-words">{selectedEvent.location}</span>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    {Icons.users('w-4 h-4 text-gray-400 flex-shrink-0')}
                    <span>{selectedEvent.confirmed_signups} signed up</span>
                  </div>

                  {selectedEvent.creator_name && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      {Icons.user('w-4 h-4 text-gray-400 flex-shrink-0')}
                      <span>Organized by {selectedEvent.creator_name} {selectedEvent.creator_lastname}</span>
                    </div>
                  )}

                  {selectedEvent.description && (
                    <p className="text-sm text-gray-700 whitespace-pre-wrap pt-1">{renderDescription(selectedEvent.description)}</p>
                  )}
                </div>

                {/* Meeting Agenda */}
                {selectedEvent.agenda && (
                  <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Meeting Agenda</h4>
                    <div className="text-gray-900 whitespace-pre-wrap text-sm">{selectedEvent.agenda}</div>
                  </div>
                )}

                {/* Post-Event Information */}
                {isEventInPast(selectedEvent) && hasPostEventInfo(selectedEvent) && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Post-Event Information</h4>
                    <div className="space-y-3">
                      {selectedEvent.post_event_notes && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Notes:</span>
                          <p className="text-gray-900 mt-1 whitespace-pre-wrap text-sm">{selectedEvent.post_event_notes}</p>
                        </div>
                      )}
                      {selectedEvent.video_recording_url && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Video Recording:</span>
                          <div className="mt-1">
                            <a
                              href={selectedEvent.video_recording_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline break-all text-sm"
                            >
                              {selectedEvent.video_recording_url}
                            </a>
                          </div>
                        </div>
                      )}
                      {selectedEvent.post_event_summary && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Summary:</span>
                          <p className="text-gray-900 mt-1 whitespace-pre-wrap text-sm">{selectedEvent.post_event_summary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* RSVP Actions - Only show for future events */}
                {!isEventInPast(selectedEvent) && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row gap-3">
                      {selectedEvent.user_rsvp_status === 'signed_up' ? (
                        <button
                          onClick={() => handleRSVP(selectedEvent.id, 'cancelled')}
                          disabled={rsvpLoading}
                          className="bg-danger-600 text-white px-4 py-2 rounded-md hover:bg-danger-700 transition-colors disabled:opacity-50"
                        >
                          {rsvpLoading ? 'Updating...' : 'RSVP No'}
                        </button>
                      ) : selectedEvent.user_rsvp_status === 'cancelled' ? (
                        <button
                          onClick={() => handleRSVP(selectedEvent.id, 'signed_up')}
                          disabled={rsvpLoading}
                          className="bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 transition-colors disabled:opacity-50"
                        >
                          {rsvpLoading ? 'RSVPing...' : 'RSVP Yes'}
                        </button>
                      ) : (
                        <>
                          {selectedEvent.can_signup && (
                            <button
                              onClick={() => handleRSVP(selectedEvent.id, 'signed_up')}
                              disabled={rsvpLoading}
                              className="bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 transition-colors disabled:opacity-50"
                            >
                              {rsvpLoading ? 'RSVPing...' : 'RSVP Yes'}
                            </button>
                          )}
                          <button
                            onClick={() => handleRSVP(selectedEvent.id, 'cancelled')}
                            disabled={rsvpLoading}
                            className="bg-danger-600 text-white px-4 py-2 rounded-md hover:bg-danger-700 transition-colors disabled:opacity-50"
                          >
                            {rsvpLoading ? 'Updating...' : 'RSVP No'}
                          </button>
                        </>
                      )}
                    </div>

                    {!selectedEvent.can_signup && !selectedEvent.user_rsvp_status && (
                      <p className="text-red-600 text-sm mt-2">This event is at capacity</p>
                    )}
                  </div>
                )}

                {/* Calendar export - always show */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href={buildGoogleCalendarUrl(selectedEvent)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-center"
                    >
                      + Google Calendar
                    </a>
                    <a
                      href={buildOutlookUrl(selectedEvent)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-center"
                    >
                      + Outlook
                    </a>
                    <button
                      onClick={() => exportEvent(selectedEvent.id)}
                      className="inline-flex items-center justify-center space-x-1.5 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                      {Icons.download('w-4 h-4')}
                      <span>Download .ics</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Calendar;
